import React, { useContext, useMemo, useState } from "react"
import { View, StyleSheet, Linking } from "react-native"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import { DimensionsContext } from "@common/utils/mobileUtils"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TouchableRippleM from "../common/components/button/TouchableRippleM"
import base64 from "../common/utils/base64"
import { useRequestOnMount } from "../common/hooks/useRequestOnMount"
import { fastTrackService } from "../data/services/fastTrackService"
import { apiRequest } from "../data/redux/actions/api"
import { connect } from "react-redux"
import { useNavigation } from "@react-navigation/native"
import {
    formatDateForGoogleCalendar,
    formatDateToMonthShort,
} from "../data/utility/Utils"
import AvatarM from "../common/components/avatar/AvatarM"
import LinkTextM from "../common/components/text/LinkTextM"
import { CongratsTemplate } from "../common/templates/CongratsTemplate"

const invitationDescription = (email, firstname) => `
Bonjour,

Voici les étapes à suivre pour se connecter au compte de ${firstname}, afin d'accéder à son cours :

Sur ordinateur :
1. Rendez-vous sur le site : app.masteur.com/login
2. Identifiant : ${email}
3. Mot de passe : celui de l'email de confirmation (ou celui que vous avez choisi)
4. Ou sans mot de passe : choisissez « Recevoir un code », saisissez cet identifiant ou le numéro du parent, puis le code reçu (il arrive sur cet email ou sur le téléphone du parent)

Sur tablette ou smartphone :
1. Téléchargez l'application "Masteur" :
   - App Store : https://apps.apple.com/app/masteur/id1614253966
   - Google Play : https://play.google.com/store/apps/details?id=com.masteur.app
2. Identifiant : ${email}
3. Mot de passe : celui de l'email de confirmation (ou celui que vous avez choisi)
4. Ou sans mot de passe : choisissez « Recevoir un code », saisissez cet identifiant ou le numéro du parent, puis le code reçu (il arrive sur cet email ou sur le téléphone du parent)

Dans les deux cas, ${firstname} devra télécharger l'application pour :
- Scanner son travail afin de le valider,
- Envoyer des exercices ou des leçons qu'il ou elle souhaite travailler avec son Masteur via le tchat privé.

À très vite,
L'équipe Masteur
`

const FastTrackThankYouScreen = (props) => {
    const dimensions = useContext(DimensionsContext)
    const containerWidth = dimensions?.width
    const emailB64 = props.route?.params?.user
    const email = emailB64 ? base64.decode(emailB64.replace(/-/g, "")) : ""
    const formGuidKey = props.route?.params?.formGuidKey ?? "subscription_1_to_5"
    const navigation = useNavigation()
    const [userTrialLiveSession, setUserTrialLiveSession] = useState(null)
    const [session, user, supportWhatsappUrl] = useMemo(
        () => [
            userTrialLiveSession?.live_sessions[0],
            userTrialLiveSession?.user,
            userTrialLiveSession?.support_whatsapp_url,
        ],
        [userTrialLiveSession]
    )

    // Detect if this is a stage/holiday_course form
    const isStageForm = formGuidKey === "holiday_course"

    const { initialLoadingHack } = useRequestOnMount(
        props.apiRequest,
        fastTrackService.getUserTrialLiveSession,
        { queryParams: { email } },
        (data) => setUserTrialLiveSession(data)
    )

    const onAddToCalendar = () => {
        const startDate = formatDateForGoogleCalendar(session.date)
        const endDate = formatDateForGoogleCalendar(
            session.date,
            session.duration
        )

        const eventData = {
            title: "Cours découverte Masteur",
            description: invitationDescription(user.email, user.firstname),
            startDate,
            endDate,
        }

        const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
            eventData.title
        )}&dates=${eventData.startDate}/${
            eventData.endDate
        }&details=${encodeURIComponent(eventData.description)}`

        Linking.openURL(googleCalendarUrl).catch(() => {
            console.log("Error opening calendar app")
        })
    }

    const renderRecapItem = ({ icon, title, value, isTutor }) => (
        <View
            style={[styles.row, { flexDirection: isTutor ? "column" : "row" }]}
        >
            {isTutor ? (
                <AvatarM size={80} user={session.tutor} />
            ) : (
                <FontAwesome5
                    name={icon}
                    size={30}
                    color={theme.colors.primary}
                />
            )}
            <View style={{ alignItems: isTutor ? "center" : "flex-start" }}>
                <TextM
                    fontSize={16}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                >
                    {title}
                </TextM>
                <TextM
                    fontSize={14}
                    color={theme.colors.grey}
                    fontWeight="Light"
                >
                    {value}
                </TextM>
            </View>
        </View>
    )

    const renderNextStep = (step, title, description) => (
        <View style={styles.stepCard}>
            <View style={styles.stepNumber}>
                <TextM
                    fontSize={16}
                    fontWeight="Bold"
                    color={theme.colors.white}
                >
                    {step}
                </TextM>
            </View>
            <View style={styles.stepContent}>
                <TextM
                    fontSize={16}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    style={styles.stepTitle}
                    wrap
                >
                    {title}
                </TextM>
                <TextM
                    fontSize={14}
                    color={theme.colors.grey}
                    style={styles.stepDescription}
                    wrap
                >
                    {description}
                </TextM>
            </View>
        </View>
    )

    return (
        <CongratsTemplate
            title="Merci, à très vite !"
            isLoading={
                initialLoadingHack || props.getUserTrialLiveSessionRequest
            }
        >
            {!!email && session && (
                <View style={styles.card}>
                    <TextM
                        fontSize={18}
                        color={theme.colors.success}
                        fontWeight="Bold"
                        wrap
                    >
                        {isStageForm
                            ? "RDV confirmé avec votre conseiller !"
                            : "RDV confirmé pour votre séance découverte !"}
                    </TextM>

                    <View style={{ width: "100%" }}>
                        <View
                            style={[
                                styles.row,
                                {
                                    marginTop: -10,
                                    justifyContent: "space-evenly",
                                },
                            ]}
                        >
                            {renderRecapItem({
                                title: session.tutor.display_name,
                                value: "Responsable pédagogique",
                                isTutor: true,
                            })}
                        </View>
                        <View
                            style={[
                                styles.row,
                                {
                                    width: "100%",
                                    marginTop: 26,
                                    justifyContent: "space-evenly",
                                },
                            ]}
                        >
                            {renderRecapItem({
                                icon: "calendar-alt",
                                title: formatDateToMonthShort(session.date),
                            })}
                            {renderRecapItem({
                                icon: "clock",
                                title: session.date.split(" ")[1],
                            })}
                        </View>
                    </View>

                    <View style={styles.buttonsStack}>
                        <TouchableRippleM
                            style={[styles.button, styles.row]}
                            onPress={onAddToCalendar}
                        >
                            <FontAwesome5
                                name="calendar-plus"
                                size={24}
                                color={theme.colors.white}
                            />
                            <TextM fontWeight="Bold" color={theme.colors.white}>
                                Ajouter à mon agenda
                            </TextM>
                        </TouchableRippleM>

                        <TouchableRippleM
                            style={[
                                styles.button,
                                styles.row,
                                styles.outlineButton,
                            ]}
                            onPress={() =>
                                navigation.navigate("login/password", {
                                    prefillEmail: email,
                                    passwordIndication: true,
                                })
                            }
                        >
                            <FontAwesome5
                                solid
                                name="user"
                                size={24}
                                color={theme.colors.success}
                            />
                            <TextM fontWeight="Bold">
                                Accéder à la plateforme
                            </TextM>
                        </TouchableRippleM>
                        <TextM fontSize={13} color={theme.colors.grey} wrap>
                            Votre mot de passe vous a été envoyé par email
                        </TextM>
                    </View>
                </View>
            )}

            {/* Visual Steps Section */}
            <View style={[styles.stepsSection]}>
                <TextM
                    fontSize={18}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    style={styles.stepsTitle}
                >
                    Les prochaines étapes
                </TextM>

                <View style={styles.stack}>
                    {isStageForm ? (
                        <>
                            {renderNextStep(
                                1,
                                "Rendez-vous conseil",
                                "Un échange avec votre conseiller pédagogique pour définir ensemble les objectifs et le contenu du stage adapté aux besoins de votre enfant."
                            )}
                            {renderNextStep(
                                2,
                                "Démarrage du stage personnalisé",
                                "Mise en place du stage sur mesure avec un programme adapté aux besoins spécifiques de votre enfant."
                            )}
                        </>
                    ) : (
                        <>
                            {renderNextStep(
                                1,
                                "Appel découverte",
                                "Appel gratuit pour répondre à vos questions avant le cours découverte."
                            )}
                            {renderNextStep(
                                2,
                                "Cours découverte",
                                "Une première séance avec un responsable pédagogique pour découvrir l'expérience Masteur et mieux comprendre les besoins de votre enfant."
                            )}
                            {renderNextStep(
                                3,
                                "Plan d'accompagnement personnalisé",
                                "Un plan d'accompagnement sur mesure adapté aux besoins spécifiques de votre enfant."
                            )}
                        </>
                    )}
                </View>
            </View>

            {/* Contact Info Card */}
            <View
                style={[
                    styles.contactCard,
                    { width: containerWidth, alignSelf: "center" },
                ]}
            >
                <TextM
                    fontSize={14}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    style={styles.contactTitle}
                >
                    Une question ?
                </TextM>
                <TextM fontSize={14} color={theme.colors.grey} wrap>
                    N&apos;hésitez pas à nous contacter{" "}
                    <LinkTextM
                        url={supportWhatsappUrl}
                        style={{ color: theme.colors.link }}
                    >
                        par WhatsApp
                    </LinkTextM>{" "}
                    ou{" "}
                    <LinkTextM
                        url="mailto:support@masteur.com"
                        style={{ color: theme.colors.link }}
                    >
                        par email
                    </LinkTextM>
                </TextM>
            </View>
        </CongratsTemplate>
    )
}

const styles = StyleSheet.create({
    subtitle: {
        textAlign: "center",
        lineHeight: 26,
    },

    card: {
        backgroundColor: theme.colors.white,
        padding: 24,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: theme.colors.light,
        width: "100%",
        alignItems: "center",
        gap: 50,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
    },
    stack: {
        width: "100%",
        alignItems: "center",
    },
    buttonsStack: {
        flexDirection: "column",
        gap: 16,
        width: "100%",
        alignItems: "center",
    },

    // Confirmation card styles
    confirmationCard: {
        backgroundColor: theme.colors.successLight,
        padding: 24,
        borderRadius: 16,
        marginBottom: 24,
        width: "100%",
        alignItems: "center",
        borderWidth: 1,
        borderColor: theme.colors.successLighter,
    },
    confirmationTitle: {
        textAlign: "center",
        marginBottom: 16,
    },
    confirmationDescription: {
        textAlign: "center",
        lineHeight: 24,
    },

    // Steps section styles
    stepsSection: {
        paddingTop: 50,
        marginBottom: 30,
        backgroundColor: theme.colors.white,
        width: "100%",
        alignItems: "center",
    },
    stepsTitle: {
        textAlign: "center",
        marginBottom: 24,
    },
    stepCard: {
        width: "100%",
        backgroundColor: theme.colors.white,
        padding: 20,
        borderRadius: 16,
        marginBottom: 16,
        flexDirection: "row",
        alignItems: "flex-start",
        elevation: 1,
        shadowColor: theme.colors.blackTransparent,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.greyLight,
    },
    stepNumber: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: theme.colors.primary,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 16,
        marginTop: 2,
    },
    stepContent: {
        flex: 1,
    },
    stepTitle: {
        marginBottom: 6,
    },
    stepDescription: {
        lineHeight: 20,
    },

    // Contact card styles
    contactCard: {
        backgroundColor: theme.colors.primaryLighter,
        padding: 20,
        borderRadius: 16,
        marginBottom: 30,
        marginTop: 30,
        alignItems: "center",
        borderWidth: 1,
        borderColor: theme.colors.primaryLighter,
    },
    contactTitle: {
        textAlign: "center",
        marginBottom: 8,
    },

    // Button styles
    backButton: {
        // Button styles now handled by LinearGradient inline styles
    },

    // Button styles
    button: {
        backgroundColor: theme.colors.success,
        padding: 10,
        borderRadius: 10,
        width: "100%",
        maxWidth: 600,
        justifyContent: "center",
        alignItems: "center",
        gap: 10,
    },
    outlineButton: {
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.success,
    },

    accordion: {
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 10,
        width: "100%",
        alignItems: "center",
    },
})

const mapStateToProps = (state) => {
    return {
        getUserTrialLiveSessionRequest:
            state.api.getUserTrialLiveSession?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FastTrackThankYouScreen)
